import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { parseDateAndTime, dateToLocalDateString } from '@/lib/dateTimeUtils';
import { PLATFORMS } from '@/lib/platformConstants';

interface ParsedQuantityData {
  date: string;
  item_name: string;
  quantity_sold: number;
  revenue?: number;
  platform: string;
  category?: string;
}

/**
 * Detects platform from header columns by checking ALL required columns from actual files
 * Also checks for Toast ProductMix format which has a specific first row format
 */
export function detectPlatformFromHeaders(headers: any[], firstCellValue?: string): 'toast' | 'uber_eats' | 'doordash' | null {
  const headerStr = headers.map(h => String(h || '').toLowerCase().trim()).join('|');
  
  // Toast Quantities - First check if this is a Toast ProductMix file
  // These files start with "ProductMix_YYYY-MM-DD_YYYY-MM-DD" in the first cell of Summary sheet
  if (firstCellValue && String(firstCellValue).toLowerCase().includes('productmix')) {
    return 'toast';
  }
  
  // DoorDash Quantities - Actual headers: Start Date, End Date, Item Name, Menu Category, Item Volume
  // Check DoorDash first because it's more specific (has dates)
  const doordashRequired = ['start date', 'end date', 'item name', 'menu category', 'item volume'];
  const hasAllDoorDash = doordashRequired.every(col => headerStr.includes(col));
  if (hasAllDoorDash) {
    return 'doordash';
  }
  
  // Uber Quantities - Actual headers: Item, Sales, Items Sold, Currency Code
  const uberRequired = ['item', 'sales', 'items sold', 'currency code'];
  const hasAllUber = uberRequired.every(col => headerStr.includes(col));
  if (hasAllUber) {
    return 'uber_eats';
  }
  
  // Toast Quantities - Also check for standard headers in case of non-ProductMix format
  // Actual headers: Menu, Menu group, Item, open item, Qty sold, Net sales
  const toastRequired = ['menu', 'menu group', 'qty sold', 'net sales'];
  const hasAllToast = toastRequired.every(col => headerStr.includes(col));
  if (hasAllToast) {
    return 'toast';
  }
  
  // Toast alternative: Check "Items" sheet headers
  // Headers: Item, Sales Category, Qty sold, Avg. price
  const toastItemsRequired = ['item', 'qty sold', 'avg. price'];
  const hasAllToastItems = toastItemsRequired.every(col => headerStr.includes(col));
  if (hasAllToastItems) {
    return 'toast';
  }
  
  return null;
}

/**
 * Parse Toast Item Quantity file
 * Supports two formats:
 * 1. "Items" sheet: Item (col 3), Sales Category (col 4), Qty sold (col 7), Avg. price (col 8)
 * 2. "Selected Levels" tab: E (Menu-4), H (Item-7), O (Qty sold-14), P (Gross Sales-15)
 * Date range is extracted from firstCellValue: ProductMix_YYYY-MM-DD_YYYY-MM-DD
 */
function parseToastQuantities(rows: any[], defaultDate?: string, firstCellValue?: string): ParsedQuantityData[] {
  const items: ParsedQuantityData[] = [];
  
  // Try to extract date from firstCellValue (format: ProductMix_2025-08-01_2025-08-31)
  let date = defaultDate || dateToLocalDateString(new Date());
  if (firstCellValue) {
    const dateMatch = String(firstCellValue).match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      date = dateMatch[1]; // Use start date from range
    }
  }

  // Detect which format we're dealing with based on headers
  const headerRow = rows[0] || [];
  const headerStr = headerRow.map((h: any) => String(h || '').toLowerCase().trim()).join('|');
  
  // Check if this is the "Items" sheet format
  const isItemsSheet = headerStr.includes('item') && headerStr.includes('qty sold') && 
                       (headerStr.includes('sales category') || headerStr.includes('avg. price'));
  
  // Check if this is the "Selected Levels" sheet format
  const isSelectedLevelsSheet = headerStr.includes('menu') && headerStr.includes('item, open item');
  
  console.log(`Toast quantities: Format detected - Items sheet: ${isItemsSheet}, Selected Levels: ${isSelectedLevelsSheet}`);
  console.log(`Toast quantities: Headers - ${headerStr.substring(0, 200)}`);

  rows.forEach((row, index) => {
    // Skip header row
    if (index === 0) return;
    
    let itemName: string | undefined;
    let quantitySold: number;
    let revenue: number;
    let category: string | undefined;
    
    if (isItemsSheet) {
      // Items sheet format:
      // Headers: masterId, parentId, itemGuid, Item, Sales Category, Item tags, Deferred, Qty sold, Avg. price, Avg. item price
      // Indices:    0         1         2       3         4              5         6         7           8            9
      itemName = row[3]; // Item column
      category = row[4]; // Sales Category column
      quantitySold = parseInt(row[7]) || 0; // Qty sold column
      const avgPrice = parseFloat(row[8]) || 0; // Avg. price column
      revenue = quantitySold * avgPrice; // Calculate revenue from qty * avg price
      
      // Skip summary rows (typically have empty itemGuid but have qty)
      if (!row[2] && !itemName) return;
    } else if (isSelectedLevelsSheet) {
      // Selected Levels format:
      // Headers: Type, masterId, parentId, itemGuid, Menu, Menu group, Subgroup, Item open item, Size modifier, ...
      // Actual data columns vary - need to find Qty sold and Gross sales columns
      const menu = row[4]; // Column E (Menu)
      itemName = row[7]; // Column H (Item, open item)
      quantitySold = parseInt(row[14]) || 0; // Column O (Qty sold)
      revenue = parseFloat(row[15]) || 0; // Column P (Gross Sales)
      category = menu;
      
      // Skip header rows
      if (itemName === 'Item, open item') return;
    } else {
      // Generic fallback - try to find item and qty columns
      // Look for non-empty cells that could be item name and quantity
      itemName = row[0] || row[3]; // First or fourth column
      quantitySold = parseInt(row[1]) || parseInt(row[7]) || 0;
      revenue = parseFloat(row[3]) || parseFloat(row[15]) || 0;
    }

    if (itemName && quantitySold > 0) {
      items.push({
        date,
        item_name: String(itemName).trim(),
        quantity_sold: quantitySold,
        revenue,
        platform: PLATFORMS.TOAST,
        category: category ? String(category).trim() : undefined
      });
    }
  });

  console.log(`Toast quantities: Parsed ${items.length} items`);
  return items;
}

/**
 * Parse Uber Item Quantity file
 * Actual columns: A (Item-0), B (Sales-1), D (Items Sold-3)
 * No date in file - uses current date or provided default
 */
function parseUberQuantities(rows: any[], defaultDate?: string): ParsedQuantityData[] {
  const items: ParsedQuantityData[] = [];
  
  const date = defaultDate || dateToLocalDateString(new Date());

  rows.forEach(row => {
    // Skip header rows
    if (!row[0] || row[0] === 'Item') return;

    const itemName = row[0]; // Column A (Item)
    const sales = parseFloat(row[1]) || 0; // Column B (Sales)
    const quantitySold = parseInt(row[3]) || 0; // Column D (Items Sold)

    if (itemName && quantitySold > 0) {
      items.push({
        date,
        item_name: itemName,
        quantity_sold: quantitySold,
        revenue: sales,
        platform: PLATFORMS.UBER
      });
    }
  });

  return items;
}

/**
 * Parse DoorDash Item Quantity file
 * Actual columns: A (Start Date-0), B (End Date-1), C (Item Name-2), D (Menu Category-3), E (Gross Item Sales-4), F (Item Volume-5)
 * Start Date comes as Excel serial number
 */
function parseDoorDashQuantities(rows: any[]): ParsedQuantityData[] {
  const items: ParsedQuantityData[] = [];

  rows.forEach((row, index) => {
    // Skip header rows
    if (index === 0 || !row[2] || row[2] === 'Item Name') return;

    const startDateRaw = row[0]; // Column A (Start Date - Excel serial)
    const itemName = row[2]; // Column C (Item Name)
    const menuCategory = row[3]; // Column D (Menu Category)
    const grossItemSales = parseFloat(row[4]) || 0; // Column E (Gross Item Sales)
    const itemVolume = parseInt(row[5]) || 0; // Column F (Item Volume)

    const parsedDate = parseDateAndTime(startDateRaw);
    const date = parsedDate.date;
    if (!date) return;

    if (itemName && itemVolume > 0) {
      items.push({
        date,
        item_name: String(itemName).trim(),
        quantity_sold: itemVolume,
        revenue: grossItemSales,
        platform: PLATFORMS.DOORDASH,
        category: menuCategory ? String(menuCategory).trim() : undefined
      });
    }
  });

  console.log(`DoorDash quantities: Parsed ${items.length} items`);
  return items;
}

/**
 * Extracts distinct POS item identifiers from a quantities file
 * Returns unique item names/identifiers for mapping purposes
 * Also aggregates quantities and revenue for each item
 */
export async function extractPOSItemIdentifiers(
  file: File
): Promise<Array<{ identifier: string; name: string; platform: string; quantity?: number; revenue?: number }>> {
  const parsedData = await processQuantitiesFile(file);
  const uniqueItems = new Map<string, { identifier: string; name: string; platform: string; quantity: number; revenue: number }>();
  
  parsedData.forEach(item => {
    const key = item.item_name.toLowerCase().trim();
    const existing = uniqueItems.get(key);
    if (!existing) {
      uniqueItems.set(key, {
        identifier: item.item_name, // Use name as identifier
        name: item.item_name,
        platform: item.platform,
        quantity: item.quantity_sold || 0,
        revenue: item.revenue || 0,
      });
    } else {
      // Aggregate quantities and revenue for items that appear multiple times
      existing.quantity += (item.quantity_sold || 0);
      existing.revenue += (item.revenue || 0);
    }
  });
  
  return Array.from(uniqueItems.values());
}

/**
 * Main processor for quantity files
 * For Toast and Uber, date must be provided as these files don't include dates
 */
export async function processQuantitiesFile(
  file: File, 
  defaultDate?: string
): Promise<ParsedQuantityData[]> {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  let rows: any[] = [];
  let workbook: any;
  let firstCellValue: string | undefined;

  if (fileExtension === 'csv') {
    const text = await file.text();
    const result = Papa.parse(text, { header: false });
    rows = result.data as any[];
    firstCellValue = rows[0]?.[0];
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    const arrayBuffer = await file.arrayBuffer();
    workbook = XLSX.read(arrayBuffer);
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
    // For Excel files, get the first cell value to help with Toast detection
    firstCellValue = rows[0]?.[0];
  } else {
    throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }

  // Detect platform from headers (also pass first cell value for Toast ProductMix detection)
  const platform = detectPlatformFromHeaders(rows[0] || [], firstCellValue);
  if (!platform) {
    // Provide helpful error message with detected headers
    const detectedHeaders = (rows[0] || []).slice(0, 8).map((h: any) => String(h || '').substring(0, 30)).join(', ');
    throw new Error(
      `Could not detect platform from file headers. Expected Toast, Uber Eats, or DoorDash quantity file. ` +
      `Found headers: ${detectedHeaders}...`
    );
  }

  console.log(`Quantities processor detected platform: ${platform}`);

  // For Toast, use "Items" or "Selected Levels" tab if it exists
  if (platform === 'toast' && workbook) {
    // First try to find "Items" sheet (more reliable data)
    const itemsSheet = workbook.SheetNames.find((name: string) => 
      name.toLowerCase() === 'items'
    );
    
    // Fall back to "Selected Levels" sheet
    const selectedLevelsSheet = workbook.SheetNames.find((name: string) => 
      name.toLowerCase().includes('selected') && name.toLowerCase().includes('level')
    );
    
    const targetSheet = itemsSheet || selectedLevelsSheet;
    if (targetSheet) {
      const sheet = workbook.Sheets[targetSheet];
      rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      console.log(`Toast quantities: Using sheet "${targetSheet}" with ${rows.length} rows`);
    }
  }

  // Process files based on platform
  // Toast: Extracts date from file content (ProductMix_YYYY-MM-DD format)
  // Uber: Uses defaultDate or current date (no date in file)
  // DoorDash: Uses date from Start Date column
  switch (platform) {
    case 'toast':
      return parseToastQuantities(rows, defaultDate, firstCellValue);
    case 'uber_eats':
      return parseUberQuantities(rows, defaultDate);
    case 'doordash':
      return parseDoorDashQuantities(rows);
    default:
      throw new Error('Unknown platform');
  }
}
